<template>
  <div ref="blockRef">
    <Component
      :is="blockIndex === 0 ? 'h1' : 'h2'"
      v-if="block.title"
      :class="`h1 -ml-[1.5%] leading-[0.9] whitespace-nowrap ${block.title_color.value}`"
      data-gsap-target="title"
    >
      <span v-html="formatTitle(block.title)" />
    </Component>

    <div class="mt-20 mx-auto px-4 max-w-6xl [@media(min-width:1920px)]:px-0">
      <div v-if="articleCategories?.length" class="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          :class="`btn ${activeArticleCategory === 'all' ? 'btn--tertiary' : 'btn--secondary'}`"
          @click="filter('all')"
        >
          <span>{{ $statamic.trans('all') }}</span>
        </button>

        <button
          v-for="articleCategory in articleCategories"
          :key="articleCategory.id"
          type="button"
          :class="`btn ${activeArticleCategory === articleCategory.slug ? 'btn--tertiary' : 'btn--secondary'}`"
          @click="filter(articleCategory.slug)"
        >
          <span>{{ articleCategory.title }}</span>
        </button>
      </div>

      <div
        v-if="articles?.length"
        class="grid grid-cols-1 gap-12 mt-8 sm:grid-cols-2 sm:mt-12 sm:pb-20 lg:gap-y-16 lg:gap-x-12 lg:mt-12 lg:pb-48"
      >
        <div
          v-for="(article, index) in articles"
          :key="article.id"
          ref="mediaItems"
          :class="{
            'lg:translate-y-32': index === 1 || (index % 2 === 1),
          }"
          data-gsap-target="mediaItem"
        >
          <MediaItem
            :url="article.url"
            :image="article.image?.sm"
            :image-srcset="`
              ${article.image?.lg.url} ${article.image?.lg.width}w,
              ${article.image?.md.url} ${article.image?.md.width}w,
              ${article.image?.sm.url} ${article.image?.sm.width}w
            `"
            image-sizes="
              (min-width: 1152px) 536px,
              (min-width: 640px) 45vw,
              95vw
            "
            :title="article.title"
            :title-below="true"
            ratio="aspect-[25/16]"
          />
        </div>
      </div>

      <div v-if="canPaginate" class="flex justify-center mt-16">
        <button
          type="button"
          class="btn btn--secondary"
          @click="paginate"
        >
          <span>{{ $statamic.trans('load_more_articles') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import uniqBy from 'lodash/uniqBy';
import flattenDeep from 'lodash/flattenDeep';
import type { Articles } from '~/statamic/types/block';
import type { Article } from '~/statamic/types/article';

defineProps<{
  block: Articles;
  blockIndex: number;
}>();

const { data, has_more_pages: hasMorePages } = await useStatamicArticles({ limit: 6 });

let ctx: gsap.Context | null;
const blockRef = ref();
const mediaItems = ref();

const articles = ref(data);
const pageNumber = ref(1);
const canPaginate = ref(hasMorePages);
const articleCategories = ref();
const activeArticleCategory = ref('all');

const uniqueArticleCategories = (items: Article[]) => {
  const categories = items.map((item) => item.article_categories);

  articleCategories.value = uniqBy(flattenDeep(categories), 'id');
};

uniqueArticleCategories(articles.value);

const paginate = async () => {
  pageNumber.value += 1;

  const { data: paginatedData, has_more_pages: paginatedDataHasMorePages } = await useStatamicArticles({
    page: pageNumber.value,
    limit: 6,
  });

  articles.value = [...articles.value, ...paginatedData];
  canPaginate.value = paginatedDataHasMorePages;

  uniqueArticleCategories(articles.value);
};

const filter = async (articleCategorySlug: string) => {
  const { data: filteredData, has_more_pages: filteredDataHasMorePages } = await useStatamicArticles({
    page: 1,
    limit: 6,
    filter: articleCategorySlug !== 'all' ? {
      // @ts-expect-error
      article_categories: {
        contains: articleCategorySlug,
      },
    } : undefined,
  });

  articles.value = filteredData;
  canPaginate.value = filteredDataHasMorePages;
  activeArticleCategory.value = articleCategorySlug;
};

watch(() => mediaItems, () => {
  const nonAnimatedMediaItems = mediaItems.value.filter((mediaItem: HTMLDivElement) => {
    return !mediaItem.classList.contains('is-animated');
  });

  nonAnimatedMediaItems.forEach((gsapMediaItem: HTMLDivElement) => {
    gsapMediaItem.classList.add('is-animated');
  });

  if (nonAnimatedMediaItems?.length) {
    useGsap.from(nonAnimatedMediaItems, {
      stagger: 0.5,
      autoAlpha: 0,
      scrollTrigger: {
        start: 'top bottom',
      },
    });
  }
}, { deep: true });

onMounted(() => {
  ctx = useGsap.context((self) => {
    if (typeof self.selector !== 'function') return;

    const titleLeft = self.selector('[data-gsap-target="title"]')[0];
    const titleRight = self.selector('[data-gsap-target="title"] > .text-right')[0];

    const timeline = useGsap.timeline({
      scrollTrigger: {
        start: 'top bottom',
        trigger: blockRef.value,
      },
    });

    if (titleLeft) {
      timeline.from(titleLeft, {
        x: -100,
        autoAlpha: 0,
      });
    }

    if (titleRight) {
      timeline.from(titleRight, {
        x: 100,
        autoAlpha: 0,
      });
    }
  }, blockRef.value);
});

onUnmounted(() => {
  ctx?.revert();
});
</script>
